import { forwardRef, useCallback, useEffect, useMemo } from 'react'

import { useEditor, useNode } from '@craftjs/core'
import { getRandomId } from '@craftjs/utils'
import { Slot } from '@radix-ui/react-slot'
import { isEmpty, mapValues, omit, uniq } from 'lodash';
import { ArrowBigUp, CopyIcon, MoveIcon, TrashIcon } from 'lucide-react'
import { cn, DraggableAction, DraggableHandle, DraggableWrapper } from 'twyne-new.ui'

import { useHTMLClassnames } from '@/components/builder/utils'
import { useFormattingProps, useInteractionMode, useSelected } from '@/modules/cms/views/post/components/builder/context'

import { useHighlight } from '../utils/Highlight';
import { useCloseParent } from '../utils/context';
import { useBlockDefinition } from '../utils/definition'
import { useCustomUtilities } from '../utils/tailwindConfig/hooks'

function useCloneNode(node) {
  const { query: { node: getNode, parseSerializedNode } } = useEditor()

  return useCallback(() => {
    const cloneNodesReducer = (newNodes, node) => {
      const serialized = getNode(node.id).toSerializedNode()
      const { nodes: childrenNodes, linkedNodes, parent, ...data } = serialized

      const newId = mapping.get(node.id)

      const newSerialized = {
        ...data,
        parent: node.id === rootNodeId ? null : mapping.get(parent),
        nodes: childrenNodes && childrenNodes.map(id => mapping.get(id)),
        linkedNodes: linkedNodes && mapValues(linkedNodes, id => mapping.get(id)),
      }

      newNodes[newId] = parseSerializedNode(newSerialized).toNode(newNode => {
        newNode.id = newId
      })

      return newNodes
    }

    const mapping = {
      get(id) {
        if (!this[id]) {
          this[id] = getRandomId()
        }
        return this[id]
      },
    }

    const { nodes, rootNodeId } = node.toNodeTree()

    return {
      nodes: Object.values(nodes).reduce(cloneNodesReducer, {}),
      rootNodeId: mapping.get(rootNodeId),
    }
  }, [node, getNode, parseSerializedNode])
}

export function useActions(id, closeParentId) {
  const { actions, query: { node: getNode } } = useEditor()

  const node = getNode(id)
  const { data: { custom: { canDuplicate, name }, parent: parentId, hidden } } = node.get()
  const index = parentId && getNode(parentId).descendants().indexOf(id)

  const isDraggable = node.isDraggable()
  const isDeletable = node.isDeletable()

  const handleSelfClone = useCloneNode(node)

  return {
    name,
    move: {
      enabled: isDraggable,
    },
    remove: {
      enabled: isDeletable,
      handler: () => actions.delete(id),
    },
    parent: {
      enabled: closeParentId && closeParentId !== 'ROOT',
      handler: () => actions.selectNode(closeParentId),
    },
    duplicate: {
      enabled: canDuplicate,
      handler: () => actions.addNodeTree(handleSelfClone(), parentId, index + 1),
    },
    visibility: {
      enabled: true,
      hidden,
      handler: () => actions.setHidden(id, !hidden),
    },
  }
}

function SelectedBehaviors() {
  useCopyProperties()
  useSelectedNode()
  return null
}

function useCopyProperties() {
  const { query: { node: getNode }, actions: { selectNode, setProp, setCustom } } = useEditor()
  const { id, selected } = useNode(({ events: { selected } }) => ({ selected }));

  const { value: interactionMode, setValue: setInteractionMode } = useInteractionMode()
  const { value: formattingProps, setValue: setFormattingProps } = useFormattingProps()
  const { getDefinition } = useBlockDefinition()

  const node = getNode(id)
  const { data: { props: nodeProps, custom: { sidebarProps } } } = useMemo(() => node.get(), [node]);

  const handleCopyProperties = useCallback(() => {
    const definitions = getDefinition(id) || []
    const ownProps = definitions.props?.filter(prop => prop.own).map(prop => prop.name)

    const { properties } = nodeProps || {}
    if (!isEmpty(properties)) {
      setFormattingProps({
        stateProps: omit(properties, ownProps),
        sidebarProps,
      })
    }
    setInteractionMode('format_painter:past')
    selectNode()
  }, [id, nodeProps, sidebarProps, selectNode, setInteractionMode, setFormattingProps, getDefinition])

  const handlePastProperties = useCallback(() => {
    const { stateProps, sidebarProps } = formattingProps || {}
    if (!isEmpty(stateProps)) {
      setProp(id, props => {
        if (isEmpty(props.properties)) return;
        Object.assign(props.properties, stateProps);
      })
    }

    if (!isEmpty(sidebarProps)) {
      setCustom(id, custom => {
        if (!custom.sidebarProps?.length) return;
        custom.sidebarProps = uniq([...custom.sidebarProps, ...sidebarProps])
      })
    }

    setInteractionMode('')
    setFormattingProps({})
  }, [id, formattingProps, setProp, setCustom, setInteractionMode, setFormattingProps])

  useEffect(() => {
    if (selected) {
      if (interactionMode === 'format_painter:copy') {
        handleCopyProperties()
      }
      if (interactionMode === 'format_painter:past') {
        handlePastProperties(selected)
      }
    }
  }, [selected, interactionMode, handleCopyProperties, handlePastProperties])
}

function useSelectedNode() {
  const { id, selected } = useNode(({ events: { selected } }) => ({ selected }));
  const { setValue: setSelected } = useSelected()

  useEffect(() => {
    if (selected) {
      setSelected(selected => {
        if (selected.length <= 1) return [id]
      })
    }
  }, [selected, id, setSelected])
}

function useAnchorClassNames() {
  const { id } = useNode()
  return useMemo(() => {
    const anchorId = Array.from(id)
      .map(char => char.charCodeAt(0).toString(16).padStart(2, '0'))
      .join('')

    return {
      source: `[anchor-name:--anchor-${anchorId}]`,
      target: `[position-anchor:--anchor-${anchorId}] top-[anchor(top)] left-[anchor(left)] right-[anchor(right)] bottom-[anchor(bottom)]`,
    }
  }, [id])
}

function useParseClassName(properties) {
  const { value: customUtilities } = useCustomUtilities()
  return useHTMLClassnames(properties, customUtilities)
}

export const Action = forwardRef(({ children, className, tag: Tag = DraggableAction, ...props }, ref) => <>
  <Tag ref={ref}
    className={cn(className, 'group-data-[highlighted=true]:bg-orange-400! group-data-[selected=true]:bg-blue-500 group-data-[selected=false]:bg-gray-500 cursor-pointer')}
    {...props}
  >
    {children}
  </Tag>
</>)

export const BasePreviewSlot = forwardRef(({ children, anchorClassName, attributes = {}, properties: { attributes: propertiesAttributes = {}, ...properties } = {}, ...props }, ref) => {
  const propertiesClassName = useParseClassName({ ...properties, ...props })

  return <Slot
    {...attributes}
    {...propertiesAttributes}
    className={`${anchorClassName} ${propertiesClassName}`}
    ref={ref}
  >
    {children}
  </Slot>
})

function DragActionsBar({ drag, id, selected, highlighted, label, customActions }) {
  const closeParentId = useCloseParent()
  const { name, move, remove, parent, duplicate } = useActions(id, closeParentId)

  const moveAction = move.enabled ? <Action ref={drag}>
    <MoveIcon size={12} />
  </Action> : null

  const deleteAction = remove.enabled ? <Action onClick={remove.handler}>
    <TrashIcon size={12} />
  </Action> : null

  const parentAction = parent.enabled ? <Action onClick={parent.handler}>
    <ArrowBigUp size={12} />
  </Action> : null

  const duplicateAction = duplicate.enabled ? <Action onClick={duplicate.handler}>
    <CopyIcon size={12} />
  </Action> : null

  const blockLabelAction = selected || highlighted ? <Action tag='div' className='text-xs text-white rounded-xs px-1.5 py-0.5' data-selected={selected || highlighted}>
    {label || name}
  </Action> : null

  return <div className='flex items-center gap-1 group' data-selected={selected} data-highlighted={highlighted}>
    <div className='flex items-center'>
      {parentAction}
      {moveAction}
      {deleteAction}
      {duplicateAction}
    </div>
    {blockLabelAction}
    {customActions && <div>
      {customActions}
    </div>}
  </div>
}

const BasePreviewDragActions = forwardRef(({ customActions, label, className }, ref) => {
  const { connectors: { drag }, id, hovered, selected } = useNode(({ events: { hovered, selected } }) => ({
    hovered, selected,
  }));
  const { highlighted, value: highlightedValue } = useHighlight({ id, selected })

  const isActive = selected || hovered || highlighted

  return <div className={cn(className, 'absolute pointer-events-none')} ref={ref}>
    <DraggableWrapper hovered={hovered} selected={selected} highlighted={highlighted} className='absolute top-[-3px] right-[-3px] bottom-[-3px] left-[-3px] pointer-events-none'>
      <div className='pointer-events-none'>
        <DraggableHandle active={isActive} className={`z-50! w-max pointer-events-auto ${highlightedValue ? 'right-[-3px]' : ''}`}>
          {isActive && <DragActionsBar drag={drag} id={id} selected={selected} highlighted={highlighted} label={label} customActions={customActions} />}
        </DraggableHandle>
      </div>
    </DraggableWrapper>
  </div>
})

export function BasePreviewWrapper({ children, ...props }) {
  const { connectors: { connect }, selected } = useNode(({ events: { selected } }) => ({ selected }))

  return <>
    {selected && <SelectedBehaviors />}
    <BasePreviewAnchor {...props} ref={connect}>
      {children}
    </BasePreviewAnchor>
  </>
}
const BasePreviewAnchor = forwardRef(({ children, customActions, label, ...props }, ref) => {
  const { source: sourceClassName, target: targetClassName } = useAnchorClassNames()

  return <>
    <BasePreviewDragActions
      className={targetClassName}
      customActions={customActions}
      label={label}
      ref={ref}
    />
    <Slot
      anchorClassName={sourceClassName}
      {...props}
      ref={ref}
    >
      {children}
    </Slot>
  </>
})

export default function BasePreview({ children, ...props }) {
  return <BasePreviewWrapper {...props}>
    <BasePreviewSlot>
      {children}
    </BasePreviewSlot>
  </BasePreviewWrapper>
}
